import type { GuestIdentity } from "@/components/primitives/guest-identity-header";
import type { AiAction } from "@/components/primitives/ai-action-panel";

/**
 * Static/mock data for the shell demo and component gallery. Phase 0 has no
 * backend to call yet (per PREMIUM_FRONTEND_PROMPT.md / TECH_STACK.md — REST
 * against the future API, not fabricated live behaviour), so every screen in
 * this package reads from here instead of a network call.
 */

export interface TenantLocation {
  id: string;
  tenantName: string;
  locationName: string;
  city: string;
}

export const MOCK_LOCATIONS: TenantLocation[] = [
  { id: "must-eatery-jeddah", tenantName: "The Must Eatery", locationName: "Jeddah — Corniche", city: "Jeddah" },
  { id: "must-eatery-riyadh", tenantName: "The Must Eatery", locationName: "Riyadh — Olaya", city: "Riyadh" },
  { id: "harbor-house-khobar", tenantName: "Harbor House", locationName: "Al Khobar — Corniche", city: "Al Khobar" },
];

export interface NavItem {
  id: string;
  label: string;
  section: "Operate" | "Grow" | "System";
}

export const NAV_ITEMS: NavItem[] = [
  { id: "today", label: "Today", section: "Operate" },
  { id: "reservations", label: "Reservations", section: "Operate" },
  { id: "floor", label: "Floor", section: "Operate" },
  { id: "guests", label: "Guests", section: "Operate" },
  { id: "inbox", label: "Inbox", section: "Operate" },
  { id: "marketing", label: "Marketing", section: "Grow" },
  { id: "community", label: "Community", section: "Grow" },
  { id: "analytics", label: "Analytics", section: "Grow" },
  { id: "settings", label: "Settings", section: "System" },
];

export const MOCK_GUEST: GuestIdentity = {
  name: "Ahmed Al-Salem",
  initials: "AA",
  language: "ar",
  channel: "whatsapp",
  valueTier: "vip",
  consent: "granted",
  lastVisit: "18 Aug",
};

export const MOCK_GUEST_PENDING_CONSENT: GuestIdentity = {
  name: "Mohammed Nasser",
  initials: "MN",
  language: "en",
  channel: "phone",
  valueTier: "gold",
  consent: "pending",
  lastVisit: "3 days ago",
};

export const MOCK_AI_ACTIONS: AiAction[] = [
  {
    id: "fill-gap",
    title: "Fill the 8:30 PM gap",
    detail: "12 covers available · high intent segment found",
    state: "suggested",
  },
  { id: "recover-call", title: "Recover missed call", detail: "Call received 7 min ago · guest identified", state: "drafted" },
  {
    id: "waitlist-upgrade",
    title: "Offer waitlist upgrade",
    detail: "5 guests match newly opened Terrace capacity",
    state: "sent-by-ai",
  },
  {
    id: "confirmed-move",
    title: "Move Mohammed Nasser to 9:00 PM",
    detail: "Reservation updated · Main Dining, 3 guests",
    state: "tool-action-confirmed",
  },
  {
    id: "review-reply",
    title: "Reply to 3-star Google review",
    detail: "Service delay mentioned — response needs manager tone review",
    state: "human-review-required",
  },
  {
    id: "campaign-send",
    title: "Send weekend campaign draft",
    detail: "WhatsApp broadcast to 210 opted-in guests",
    state: "failed",
  },
];

export const MOCK_ATTENTION_ITEMS = [
  { id: "unconfirmed", title: "2 unconfirmed bookings", description: "Dinner service starts in 90 minutes." },
  { id: "missed-call", title: "1 missed phone call", description: "AI identified likely booking intent 7 minutes ago." },
  { id: "whatsapp", title: "3 WhatsApp enquiries", description: "Awaiting first response — oldest is 22 minutes old." },
];

export const CURRENT_USER = {
  name: "Sajjad Ali",
  initials: "SA",
  role: "Restaurant Manager",
};
